import { api } from "@/lib/apiClient";
import { addDaysLocal } from "@/lib/bookingDates";
import type { PlannerCustomEntry } from "@/types/camping";

export const PLANNER_EVENTS_LABEL = "Veranstaltungen";

export interface RegionHoliday {
  date: string;
  name: string;
}

export interface RegionCalendar {
  postal_code: string | null;
  city: string | null;
  state_code: string | null;
  state_name: string | null;
  basis: "state" | "nationwide" | "unavailable";
  place_label: string;
  holidays: RegionHoliday[];
}

export interface StayCalendarNote {
  id: string;
  kind: "event" | "holiday";
  title: string;
}

export function isPlannerEventsLabel(label: string): boolean {
  return label.trim().toLowerCase() === PLANNER_EVENTS_LABEL.toLowerCase();
}

/** Aufenthalt inklusive Abreisetag schneidet [start, endExclusive). */
export function stayOverlapsRange(
  arrival: string,
  departure: string,
  start: string,
  endExclusive: string,
): boolean {
  const stayEndExclusive = addDaysLocal(departure, 1);
  return start < stayEndExclusive && endExclusive > arrival;
}

export function notesForStay(
  arrival: string,
  departure: string,
  holidays: RegionHoliday[],
  entries: PlannerCustomEntry[],
): StayCalendarNote[] {
  const holidayNotes: StayCalendarNote[] = holidays
    .filter((holiday) =>
      stayOverlapsRange(arrival, departure, holiday.date, addDaysLocal(holiday.date, 1)),
    )
    .map((holiday) => ({
      id: `holiday-${holiday.date}`,
      kind: "holiday" as const,
      title: holiday.name,
    }));
  const eventNotes: StayCalendarNote[] = entries
    .filter(
      (entry) =>
        isPlannerEventsLabel(entry.label) &&
        stayOverlapsRange(arrival, departure, entry.start_date, entry.end_date),
    )
    .map((entry) => ({
      id: entry.id,
      kind: "event" as const,
      title: entry.title,
    }));
  return [...holidayNotes, ...eventNotes];
}

export async function loadRegionCalendar(from: string, toExclusive: string): Promise<RegionCalendar | null> {
  try {
    return await api.get<RegionCalendar>(
      `/api/v1/planner/region-calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(toExclusive)}`,
    );
  } catch {
    return null;
  }
}

export async function loadStayCalendarNotes(
  arrival: string,
  departure: string,
): Promise<StayCalendarNote[]> {
  const toExclusive = addDaysLocal(departure, 1);
  const [calendar, entries] = await Promise.all([
    loadRegionCalendar(arrival, toExclusive),
    api
      .get<PlannerCustomEntry[]>(
        `/api/v1/planner/custom-entries?from=${encodeURIComponent(arrival)}&to=${encodeURIComponent(toExclusive)}`,
      )
      .catch(() => [] as PlannerCustomEntry[]),
  ]);
  return notesForStay(arrival, departure, calendar?.holidays ?? [], entries);
}
